import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Copy } from 'react-feather';
import { toast } from 'react-hot-toast';
import { sneakylinkService, SneakylinkClickResponse } from '../../services/sneakylink.service';

export function SneakylinkRedirect() {
  const { token } = useParams<{ token: string }>();
  const [codeData, setCodeData] = useState<SneakylinkClickResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [codeCount, setCodeCount] = useState<number>(1);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate requests (especially in React 18 Strict Mode)
    if (token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSneakylinkData(1);
    }
  }, [token]);

  const fetchSneakylinkData = async (count: number) => {
    try {
      setLoading(true);
      const response = await sneakylinkService.processSneakylink(token!, count);
      setCodeData(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid or expired sneakylink');
      setCodeData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (codeData) {
      const codes = codeData.codes && codeData.codes.length > 0 ? codeData.codes : [codeData.code];
      await navigator.clipboard.writeText(codes.join(', '));
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Generating your exclusive discount code...</p>
        </div>
      </div>
    );
  }

  if (error || !codeData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="text-center max-w-md px-4">
          <div className="bg-card rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Link Expired or Invalid
            </h1>
            <p className="text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-blue-50 p-4">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-xl p-8 space-y-6">
        {/* Brand Logo */}
        {codeData.brandLogo && (
          <div className="flex justify-center">
            <img 
              src={codeData.brandLogo} 
              alt={codeData.brandName}
              className="h-16 object-contain"
            />
          </div>
        )}
        
        {/* Headline */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Your Exclusive Discount Code
          </h1>
          <p className="text-muted-foreground">
            Get {codeData.discountValue}
            {codeData.discountType === 'PERCENTAGE' ? '%' : '$'} off at {codeData.brandName}
          </p>
        </div>

        {/* Code count selector */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-muted-foreground text-center">
            Number of one-time codes to generate (max 10)
          </label>
          <div className="flex items-center justify-center gap-3">
            <input
              type="number"
              min={1}
              max={10}
              value={codeCount}
              onChange={(e) => {
                const value = parseInt(e.target.value || '1', 10);
                if (Number.isNaN(value)) {
                  setCodeCount(1);
                } else {
                  setCodeCount(Math.min(Math.max(value, 1), 10));
                }
              }}
              className="w-20 text-center border border-border rounded-md py-1 px-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={() => fetchSneakylinkData(codeCount)}
              className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 text-sm"
            >
              Generate codes
            </button>
          </div>
        </div>

        {/* Discount Code Display */}
        <div className="bg-gradient-to-r from-brand-100 to-blue-100 rounded-xl p-6 space-y-3">
          <p className="text-sm text-muted-foreground text-center mb-2">
            {codeData.codes && codeData.codes.length > 1 ? 'Your Codes:' : 'Your Code:'}
          </p>
          <div className="bg-card rounded-lg p-4">
            {codeData.codes && codeData.codes.length > 1 ? (
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {codeData.codes.map((c) => (
                  <li
                    key={c}
                    className="text-lg font-semibold text-brand-600 tracking-wider text-center"
                  >
                    {c}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-3xl font-bold text-brand-600 tracking-wider text-center">
                {codeData.code}
              </p>
            )}
          </div>
        </div>

        {/* Copy Button */}
        <button
          onClick={handleCopyCode}
          className="w-full py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors flex items-center justify-center gap-2"
        >
          {copied ? (
            <>
              <Check className="h-5 w-5" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-5 w-5" />
              Copy {codeData.codes && codeData.codes.length > 1 ? 'All Codes' : 'Code'}
            </>
          )}
        </button>

        {/* Expiry notice */}
        <p className="text-center text-xs text-muted-foreground">
          Codes expire {new Date(codeData.expiresAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

